import { useEffect, useState } from "react";

import {
  getProratedAmountAfterChargeDateShift,
  getShiftedSubscriptionChargeDate,
  getShiftedWeekdayChargeDate,
  IsoLocale,
  isLastDayOfMonthChargeDate,
  isMonthBasedSubscriptionInterval,
  LAST_DAY_OF_MONTH,
  Locales,
  MAX_CHARGE_DAY_OF_MONTH,
  SubscriptionRecurrenceInterval,
  toChargeTimezoneDateTime,
} from "@simplyk/common";

import { Translate } from "@/common/hooks/useTranslate";

export type ChargeDayOfMonth = number | typeof LAST_DAY_OF_MONTH;

export interface ChargeDateChange {
  day?: ChargeDayOfMonth;
  month?: number;
  weekday?: number;
}

export const useEditChargeDate = ({
  nextChargeDate,
  recurrenceInterval,
  chargeAmount,
  timezone,
}: {
  nextChargeDate: Date | string;
  recurrenceInterval: SubscriptionRecurrenceInterval;
  chargeAmount?: number;
  timezone?: string | null;
}) => {
  const isYearly = recurrenceInterval === SubscriptionRecurrenceInterval.Yearly;
  const isMonthly = recurrenceInterval === SubscriptionRecurrenceInterval.Monthly;
  const monthBasedInterval = isMonthBasedSubscriptionInterval(recurrenceInterval) ? recurrenceInterval : null;
  const currentChargeDate = new Date(nextChargeDate);
  const currentCharge = toChargeTimezoneDateTime(currentChargeDate, timezone);
  const currentDay = currentCharge.day;
  const currentMonth = currentCharge.month;
  const currentWeekday = currentCharge.weekday;

  const initialDay: ChargeDayOfMonth | null =
    isMonthly && isLastDayOfMonthChargeDate(currentChargeDate)
      ? LAST_DAY_OF_MONTH
      : currentDay <= MAX_CHARGE_DAY_OF_MONTH
        ? currentDay
        : null;

  const [selectedDay, setSelectedDay] = useState<ChargeDayOfMonth | null>(initialDay);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [viewedMonth, setViewedMonth] = useState(currentMonth);
  const [selectedWeekday, setSelectedWeekday] = useState(currentWeekday);

  const chargeDateTimestamp = currentChargeDate.getTime();
  useEffect(() => {
    setSelectedDay(initialDay);
    setSelectedMonth(currentMonth);
    setViewedMonth(currentMonth);
    setSelectedWeekday(currentWeekday);
  }, [chargeDateTimestamp, initialDay, currentMonth, currentWeekday]);

  const hasChanged = monthBasedInterval
    ? selectedDay !== null && (selectedDay !== initialDay || (isYearly && selectedMonth !== currentMonth))
    : selectedWeekday !== currentWeekday;

  const previewDate = monthBasedInterval
    ? getShiftedSubscriptionChargeDate({
        currentNextChargeDate: nextChargeDate,
        day: selectedDay ?? currentDay,
        month: isYearly ? selectedMonth : undefined,
        recurrenceInterval: monthBasedInterval,
        timezone,
      })
    : getShiftedWeekdayChargeDate({
        currentNextChargeDate: nextChargeDate,
        weekday: selectedWeekday,
        recurrenceInterval,
        timezone,
      });

  const isProrated = hasChanged && previewDate.getTime() < currentChargeDate.getTime();
  const proratedAmount =
    isProrated && chargeAmount !== undefined
      ? getProratedAmountAfterChargeDateShift({
          currentNextChargeDate: nextChargeDate,
          newChargeDate: previewDate,
          recurrenceInterval,
          amount: chargeAmount,
        })
      : null;

  const selectDay = (day: ChargeDayOfMonth) => {
    setSelectedDay(day);
    if (isYearly) {
      setSelectedMonth(viewedMonth);
    }
  };

  const goToPreviousMonth = () => setViewedMonth((month) => (month === 1 ? 12 : month - 1));
  const goToNextMonth = () => setViewedMonth((month) => (month === 12 ? 1 : month + 1));

  const reset = () => {
    setSelectedDay(initialDay);
    setSelectedMonth(currentMonth);
    setViewedMonth(currentMonth);
    setSelectedWeekday(currentWeekday);
  };

  const chargeDateChange: ChargeDateChange | null = !hasChanged
    ? null
    : monthBasedInterval
      ? { day: selectedDay ?? undefined, month: isYearly ? selectedMonth : undefined }
      : { weekday: selectedWeekday };

  return {
    isYearly,
    isMonthly,
    currentDay,
    isWeekBased: !monthBasedInterval,
    initialDay,
    currentMonth,
    currentWeekday,
    selectedDay,
    selectedMonth,
    selectedWeekday,
    viewedMonth,
    hasChanged,
    chargeDateChange,
    previewDate,
    isProrated,
    proratedAmount,
    selectDay,
    selectWeekday: setSelectedWeekday,
    goToPreviousMonth,
    goToNextMonth,
    reset,
  };
};

export const formatMonthName = (month: number, locale: Locales | IsoLocale): string =>
  new Date(Date.UTC(2000, month - 1, 1)).toLocaleString(locale, { month: "long", timeZone: "UTC" });

export const getEditChargeDateErrorMessage = (t: Translate, errorCode: string | undefined): string =>
  errorCode === "charge_date_after_scheduled_end"
    ? t("dashboard", "editChargeDateDialog.errorAfterScheduledEnd")
    : t("dashboard", "editChargeDateDialog.error");
